// Which fields the validator-recovery modal asks for, and their schemas.
// Dependency-free on purpose: the node suite imports this file directly
// (the modal pulls in @app/@platform aliases that only Vite resolves).

// Lowercase + strip trailing/leading whitespace + drop any trailing
// punctuation. Some Jira fields are configured with a trailing colon
// (e.g. "Responsible Engineer:") for display purposes, but the
// validator emits the bare name ("Responsible Engineer") — so we
// normalise both sides before comparing.
export function normalizeFieldName(s) {
  return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+$/, '');
}

// Stride's field store keeps only schema_type ('array') and schema_custom,
// so createmeta and the registry send an array field WITHOUT `items` — and
// FieldInput, which picks its control by items, fell through to a plain text
// box for a multi-select (Epic's "Responsible Engineer:"). Derive items from
// the custom type key when it is missing.
const ITEMS_BY_CUSTOM = {
  multiselect: 'option', multicheckboxes: 'option',
  multiuserpicker: 'user', labels: 'string',
  multiversion: 'version',
};
export function inferItems(type, items, custom) {
  if (items || type !== 'array') return items;
  const key = String(custom || '').split(':').pop();
  return ITEMS_BY_CUSTOM[key];
}

// A field whose value is another work item's key: the system Parent field
// (Stride stores it as schema_type 'issuelink') or the legacy Epic Link.
// FieldInput renders a work-item picker for these instead of a text box.
// Returns 'parent' (sent as { key }), 'epic-link' (sent as the bare key) or null.
export function issuePickerKind(fieldId, schema) {
  if (fieldId === 'parent' || schema?.system === 'parent' || schema?.type === 'issuelink') return 'parent';
  if (String(schema?.custom || '').endsWith(':gh-epic-link')) return 'epic-link';
  return null;
}

// The create-payload value for a work-item-picker field, or undefined when empty.
export function issuePickerValue(kind, value) {
  const key = String(value || '').trim().toUpperCase();
  if (!key) return undefined;
  return kind === 'parent' ? { key } : key;
}

// Build a flat field def (the shape FieldInput reads) from a global
// field-registry row (api.getFields: { id, name, schema:{type,custom} }).
export function fieldDefFromRegistry(reg) {
  return {
    name: reg?.name,
    type: reg?.schema?.type,
    items: inferItems(reg?.schema?.type, reg?.schema?.items, reg?.schema?.custom),
    system: reg?.schema?.system,
    custom: reg?.schema?.custom,
    allowedValues: undefined,
  };
}

// fieldId -> flat field def, from api.getCreateMeta. /api/createmeta
// returns an OBJECT keyed by fieldId with the schema already flattened
// ({ name, type, items, system, custom, allowedValues }) — every create
// screen reads it that way. This modal used to read only the internal
// ARRAY form ([{ fieldId, schema:{type}, allowedValues }]), so it skipped
// every entry, matched no validator message to a field, and showed a
// fillable field (e.g. Epic's "Responsible Engineer:") as bare text.
// Both shapes are accepted.
export function schemaMapFromCreateMeta(meta) {
  const map = {};
  if (Array.isArray(meta)) {
    for (const f of meta) {
      if (!f?.fieldId) continue;
      map[f.fieldId] = {
        name: f.name,
        type: f.schema?.type,
        items: inferItems(f.schema?.type, f.schema?.items, f.schema?.custom),
        system: f.schema?.system,
        custom: f.schema?.custom,
        allowedValues: f.allowedValues,
      };
    }
    return map;
  }
  for (const [fieldId, f] of Object.entries(meta || {})) {
    if (!f || typeof f !== 'object') continue;
    map[fieldId] = {
      name: f.name,
      type: f.type,
      items: inferItems(f.type, f.items, f.custom),
      system: f.system,
      custom: f.custom,
      allowedValues: f.allowedValues,
    };
  }
  return map;
}

// Pull "field name" tokens out of one validator message and pick the
// first one that maps to a known field. Returns { fieldId, schema } or
// null. We accept any quoted token (either single or double quotes —
// different Jira validator plugins use different conventions) so this
// handles the common "Field X must be filled in" *and* less common
// shapes like `'Field X' must be one of: A, B, C`. The backreference
// ensures the opening and closing quote characters match. A name not on
// the create screen falls back to the global field registry.
function findFieldInMessage(message, schemas, registry, alreadyMatched) {
  if (!message) return null;
  const quoted = [...String(message).matchAll(/(['"])([^'"]+?)\1/g)].map(m => m[2]);
  const nameToId = {};
  for (const [fieldId, s] of Object.entries(schemas || {})) {
    if (s?.name) nameToId[normalizeFieldName(s.name)] = fieldId;
  }
  const registryByName = {};
  for (const [fieldId, r] of Object.entries(registry || {})) {
    const n = normalizeFieldName(r?.name);
    if (n && registryByName[n] === undefined) registryByName[n] = fieldId;
  }
  for (const candidate of quoted) {
    const n = normalizeFieldName(candidate);
    const fieldId = nameToId[n];
    if (fieldId && !alreadyMatched.has(fieldId)) {
      return { fieldId, schema: schemas[fieldId] };
    }
    const regId = registryByName[n];
    if (regId && !alreadyMatched.has(regId)) {
      return { fieldId: regId, schema: fieldDefFromRegistry(registry[regId]) };
    }
  }
  return null;
}

// Walk a list of validator messages and split into fillable fields
// (with schema) vs unparseable raw messages.
export function parseValidatorMessages(messages, schemas, registry) {
  const fillable = [];
  const unparseable = [];
  const matched = new Set();
  for (const msg of (messages || [])) {
    const hit = findFieldInMessage(msg, schemas, registry, matched);
    if (hit) {
      fillable.push({ ...hit, sourceMessage: msg });
      matched.add(hit.fieldId);
    } else {
      unparseable.push(String(msg));
    }
  }
  return { fillable, unparseable };
}

// Build the fillable-field list directly from the create-gate's deduped
// list of required field ids (`missingFields`). Prefers the create-screen
// schema map; for a required field that isn't on the create screen, falls
// back to the global field registry, then to a bare text input labelled by
// the id. This bypasses the fragile validator-message-text regex entirely.
export function buildFillableFromMissing(missingFields, schemas, registry) {
  const fillable = [];
  const seen = new Set();
  for (const rawId of (missingFields || [])) {
    const fieldId = String(rawId || '');
    if (!fieldId || seen.has(fieldId)) continue;
    seen.add(fieldId);
    let schema = schemas?.[fieldId];
    if (!schema) {
      const reg = registry?.[fieldId];
      schema = reg ? fieldDefFromRegistry(reg) : { name: fieldId, type: 'string' };
    }
    fillable.push({ fieldId, schema });
  }
  return fillable;
}
